import { memo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Lesson, Resubmission } from '@/entities/Courses';
import { Svg } from '@/shared/ui/Svg';
import MoreVerticalIcon from '@/shared/assets/icons/more-vertical.svg';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import ChecklistIcon from '@/shared/assets/icons/checklist.svg';
import EditIcon from '@/shared/assets/icons/edit.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import styles from './SessionActionsMenu.module.scss';

const MENU_ESTIMATE_HEIGHT = 340;

type Session = Lesson | Resubmission;

interface SessionActionsMenuProps {
  session: Session;
  onView?: (session: Session) => void;
  onMarkAttendance?: (session: Session) => void;
  onEdit?: (session: Session) => void;
  onDuplicate?: (session: Session) => void;
  onDelete?: (session: Session) => void;
}

export const SessionActionsMenu = memo(
  ({ session, onView, onMarkAttendance, onEdit, onDuplicate, onDelete }: SessionActionsMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{
      top: number;
      right: number;
      placement: 'bottom' | 'top';
    } | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
      if (!isOpen || !rootRef.current) return;
      const rect = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < MENU_ESTIMATE_HEIGHT && rect.top >= MENU_ESTIMATE_HEIGHT;

      if (openAbove) {
        setMenuPosition({
          top: rect.top - MENU_ESTIMATE_HEIGHT + 100,
          right: window.innerWidth - rect.right,
          placement: 'top',
        });
      } else {
        setMenuPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
          placement: 'bottom',
        });
      }
    }, [isOpen]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          rootRef.current &&
          menuRef.current &&
          !rootRef.current.contains(event.target as Node) &&
          !menuRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setMenuPosition(null);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) return;
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [isOpen, updatePosition]);

    const handleToggle = () => {
      if (isOpen) {
        setIsOpen(false);
        setMenuPosition(null);
        return;
      }
      if (!rootRef.current) {
        setIsOpen(true);
        return;
      }
      const rect = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < MENU_ESTIMATE_HEIGHT && rect.top >= MENU_ESTIMATE_HEIGHT;
      setMenuPosition(
        openAbove
          ? {
              top: rect.top - MENU_ESTIMATE_HEIGHT - 8,
              right: window.innerWidth - rect.right,
              placement: 'top',
            }
          : {
              top: rect.bottom + 8,
              right: window.innerWidth - rect.right,
              placement: 'bottom',
            },
      );
      setIsOpen(true);
    };

    const handleEdit = () => {
      onEdit?.(session);
      setIsOpen(false);
      setMenuPosition(null);
    };

    const handleView = () => {
      onView?.(session);
      setIsOpen(false);
      setMenuPosition(null);
    };

    const handleMarkAttendance = () => {
      onMarkAttendance?.(session);
      setIsOpen(false);
      setMenuPosition(null);
    };

    const handleDuplicate = () => {
      onDuplicate?.(session);
      setIsOpen(false);
      setMenuPosition(null);
    };

    const handleDelete = () => {
      onDelete?.(session);
      setIsOpen(false);
      setMenuPosition(null);
    };

    const isResubmission = (session as { _kind?: string })._kind === 'resubmission';
    const canMarkAttendance = Boolean(onMarkAttendance) && !isResubmission;

    return (
      <>
        <div className={styles.root} ref={rootRef}>
          <button
            type="button"
            className={styles.trigger}
            onClick={handleToggle}
            aria-label="Session actions"
          >
            <Svg Icon={MoreVerticalIcon} width={20} height={20} className="text-grey-secondary" />
          </button>
        </div>
        {isOpen &&
          menuPosition &&
          createPortal(
            <div
              ref={menuRef}
              className={styles.menu}
              style={{
                top: `${menuPosition.top}px`,
                right: `${menuPosition.right}px`,
              }}
              data-placement={menuPosition.placement}
            >
              <button type="button" className={styles.menuItem} onClick={handleView}>
                <Svg Icon={OpenedEyeIcon} width={20} height={20} />
                <span>Preview</span>
              </button>
              {canMarkAttendance && (
                <button type="button" className={styles.menuItem} onClick={handleMarkAttendance}>
                  <Svg Icon={ChecklistIcon} width={20} height={20} />
                  <span>Mark attendance</span>
                </button>
              )}
              {onEdit && (
                <button type="button" className={styles.menuItem} onClick={handleEdit}>
                  <Svg Icon={EditIcon} width={20} height={20} />
                  <span>Edit</span>
                </button>
              )}
              {/*<button type="button" className={styles.menuItem} onClick={handleDuplicate}>*/}
              {/*  <Svg Icon={CopyIcon} width={20} height={20} />*/}
              {/*  <span>Duplicate</span>*/}
              {/*</button>*/}
              {onDelete && (
                <button
                  type="button"
                  className={`${styles.menuItem} ${styles.deleteItem}`}
                  onClick={handleDelete}
                >
                  <Svg Icon={TrashIcon} width={20} height={20} />
                  <span>Delete</span>
                </button>
              )}
            </div>,
            document.body,
          )}
      </>
    );
  },
);

SessionActionsMenu.displayName = 'SessionActionsMenu';
